import React, { useMemo, useState } from 'react';
import { Button, Card, Cascader, Input, InputNumber, Popconfirm, Space, Tag, Tooltip, message } from 'antd';
import { AimOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
    ADD_AOI_RELATED_ADDRESSES,
    AddAoiRelatedAddressesResponse,
    AoiAddRelatedAddressItem,
    AoiAddRelatedAddressesInput,
} from '../graphql/mutations/addAoiRelatedAddresses';
import { useTranslation } from 'react-i18next';
import { AOI_DETAIL_COLORS, MAX_LIST_SIZE, PickTarget } from '../constants';
import {
    AoiTypeDictItem,
    LIST_AOI_TYPE_DICTIONARY,
    ListAoiTypeDictionaryResponse,
} from '../graphql/queries/listAoiTypeDictionary';
import {
    BizAoiAttributeDetail,
    BizAoiAttributeLocationPoint,
} from '../graphql/queries/getAoiAttributeDetail';
import { DeliveryTime, parseDeliveryTime } from '../utils/deliveryTime';
import DeliveryTimeEditor from './DeliveryTimeEditor';
import AccessCodeEditor, {
    AccessCodeDraft,
    emptyAccessCodeDraft,
} from './AccessCodeEditor';

/**
 * 编辑面板表单的"草稿"状态 - 用户在编辑模式下的所有改动暂存在这里。
 * 保存时父组件读取并转成 GraphQL mutation 入参。
 *
 * <p>注: `deliveryTime` 在草稿里是结构化对象, 保存时由父组件序列化成 JSON 字符串。
 * <p>注: `accessCodes` 是 AOI 自己的门禁, 跟其他字段一起随 upsertAoiAttribute 全量提交, 不做 diff。
 */
export interface AoiAttributeDraft {
    typeCode: string | null;
    entryPoints: BizAoiAttributeLocationPoint[];
    parkingPoints: BizAoiAttributeLocationPoint[];
    deliveryTime: DeliveryTime;
    /** 当前 UI 上的门禁 code 全量列表 (AOI 级, 全量覆盖语义)。 */
    accessCodes: AccessCodeDraft[];
}

interface Props {
    /** 当前 AOI 详情, 用于回显只读字段 + 初始化草稿。 */
    detail: BizAoiAttributeDetail;
    /** 受控的编辑草稿 (由父组件持有, 这里只读 + 通过 onDraftChange 推回去)。 */
    draft: AoiAttributeDraft;
    /** 草稿变更回调。 */
    onDraftChange: (next: AoiAttributeDraft) => void;
    /** 当前正在拾取的点类型 (null = 未在拾取模式)。 */
    pickTarget: PickTarget;
    /** 用户点击 `+ 新增` 时触发, 父组件把 pickTarget 切换到对应目标。 */
    onStartPick: (target: 'entryPoints' | 'parkingPoints') => void;
    /** 关联地址新增成功后调用, 让父组件 refetch 详情, 拿到最新 relatedAddresses。 */
    onRefetchDetail?: () => void;
}

/**
 * AOI 详情 - 编辑面板。
 *
 * <p>实现的可编辑字段:
 * <ul>
 *   <li>类型: 一级 / 二级 级联选择 (从 listAoiTypeDictionary GraphQL 拉);</li>
 *   <li>进门点: 列表 CRUD, 经纬度由地图拾取产生 (`+ 新增` 进入拾取模式);</li>
 *   <li>停车点: 同进门点;</li>
 *   <li>配送时间: JSON 字符串输入 (结构由后续业务规则定义)。</li>
 * </ul>
 *
 * <p>拾取模式: `+ 新增` 不再直接 append 默认坐标的点, 而是请父组件切换 pickTarget,
 * 由地图组件接管点击事件, 用户确认后父组件把坐标回写到 draft.entryPoints/parkingPoints。
 * 拾取模式开启时, "另一种点位" 的 `+ 新增` 按钮 disabled, 避免互相争抢地图点击。
 */
const AoiAttributeEditPanel: React.FC<Props> = ({
    detail,
    draft,
    onDraftChange,
    pickTarget,
    onStartPick,
    onRefetchDetail,
}) => {
    const { t } = useTranslation();

    const updateDraft = (patch: Partial<AoiAttributeDraft>) => {
        onDraftChange({ ...draft, ...patch });
    };

    /* ====================== 类型字典 (级联) ====================== */
    const { data: dictData } = useQuery<ListAoiTypeDictionaryResponse>(LIST_AOI_TYPE_DICTIONARY, {
        fetchPolicy: 'cache-first',
    });

    const cascaderOptions = useMemo(
        () => buildCascaderOptions(dictData?.listAoiTypeDictionary ?? []),
        [dictData],
    );

    // typeCode (叶子) → cascader 的 [一级, 二级] 路径 (一级单独叶子时只有 [一级])
    const cascaderValue = useMemo(() => {
        if (!draft.typeCode || !dictData) return undefined;
        const node = dictData.listAoiTypeDictionary.find((d) => d.code === draft.typeCode);
        if (!node) return undefined;
        return node.parentCode ? [node.parentCode, node.code] : [node.code];
    }, [draft.typeCode, dictData]);

    /* ====================== 进门点 / 停车点 通用 CRUD ====================== */
    const removePoint = (key: 'entryPoints' | 'parkingPoints', idx: number) => {
        const list = draft[key].filter((_, i) => i !== idx);
        updateDraft({ [key]: list } as Partial<AoiAttributeDraft>);
    };
    const updatePoint = (
        key: 'entryPoints' | 'parkingPoints',
        idx: number,
        patch: Partial<BizAoiAttributeLocationPoint>,
    ) => {
        const list = draft[key].map((p, i) => (i === idx ? { ...p, ...patch } : p));
        updateDraft({ [key]: list } as Partial<AoiAttributeDraft>);
    };

    // 当前是否处于本面板触发的拾取模式 (用于禁用另一个 `+ 新增` 避免误触)
    const isPickingEntry = pickTarget === 'entryPoints';
    const isPickingParking = pickTarget === 'parkingPoints';

    /* ====================== 渲染 ====================== */
    const isBuilding = detail.level === 0;
    const isCommunity = detail.level === 1;

    return (
        <div className="space-y-3 overflow-y-auto h-full pr-1">
            {/* 基础信息 (只读) */}
            <Card size="small" title="基础信息 (不可编辑)">
                <InfoRow label="AOI ID" value={String(detail.aoiId)} />
                <InfoRow label="层级" value={
                    detail.level === 0
                        ? <Tag color="blue">{t('mapTool.aoiPlatform.options.levelBuilding')}</Tag>
                        : detail.level === 1
                            ? <Tag color="purple">{t('mapTool.aoiPlatform.options.levelCommunity')}</Tag>
                            : '-'
                } />
                <InfoRow label="中心点" value={
                    detail.centerLatitude != null && detail.centerLongitude != null
                        ? `${detail.centerLatitude.toFixed(7)}, ${detail.centerLongitude.toFixed(7)}`
                        : '-'
                } />
                <InfoRow label="邮编" value={detail.zipcode ?? '-'} />
            </Card>

            {/* 类型 - 支持级联选择, 也支持显式选 "无类型" (空类型) */}
            <Card size="small" title="类型">
                <div className="flex gap-2 items-center">
                    <Cascader
                        className="flex-1"
                        options={cascaderOptions}
                        value={cascaderValue}
                        onChange={(val) => {
                            const leaf = (val && val.length > 0) ? String(val[val.length - 1]) : null;
                            updateDraft({ typeCode: leaf });
                        }}
                        placeholder="请选择类型 (一级 / 二级)"
                        changeOnSelect
                        allowClear
                    />
                    <Button
                        size="middle"
                        type={draft.typeCode == null ? 'primary' : 'default'}
                        onClick={() => updateDraft({ typeCode: null })}
                    >
                        无类型
                    </Button>
                </div>
            </Card>

            {/* 进门点 */}
            <Card
                size="small"
                title={<EditorSectionTitle text="进门点" color={AOI_DETAIL_COLORS.entryPoint} count={draft.entryPoints.length} max={MAX_LIST_SIZE} />}
                extra={
                    <PickAddButton
                        active={isPickingEntry}
                        disabled={isPickingParking || draft.entryPoints.length >= MAX_LIST_SIZE}
                        disabledReason={
                            draft.entryPoints.length >= MAX_LIST_SIZE
                                ? `已达上限 ${MAX_LIST_SIZE} 条`
                                : '另一类点位正在拾取中, 请先确定或取消'
                        }
                        onClick={() => onStartPick('entryPoints')}
                    />
                }
            >
                <PointsEditor
                    points={draft.entryPoints}
                    onChange={(idx, patch) => updatePoint('entryPoints', idx, patch)}
                    onRemove={(idx) => removePoint('entryPoints', idx)}
                />
            </Card>

            {/* 停车点 */}
            <Card
                size="small"
                title={<EditorSectionTitle text="停车点" color={AOI_DETAIL_COLORS.parkingPoint} count={draft.parkingPoints.length} max={MAX_LIST_SIZE} />}
                extra={
                    <PickAddButton
                        active={isPickingParking}
                        disabled={isPickingEntry || draft.parkingPoints.length >= MAX_LIST_SIZE}
                        disabledReason={
                            draft.parkingPoints.length >= MAX_LIST_SIZE
                                ? `已达上限 ${MAX_LIST_SIZE} 条`
                                : '另一类点位正在拾取中, 请先确定或取消'
                        }
                        onClick={() => onStartPick('parkingPoints')}
                    />
                }
            >
                <PointsEditor
                    points={draft.parkingPoints}
                    onChange={(idx, patch) => updatePoint('parkingPoints', idx, patch)}
                    onRemove={(idx) => removePoint('parkingPoints', idx)}
                />
            </Card>

            {/* 配送时间 - 结构化编辑器 */}
            <Card size="small" title="配送时间">
                <DeliveryTimeEditor
                    value={draft.deliveryTime}
                    onChange={(next) => updateDraft({ deliveryTime: next })}
                />
                <div className="text-xs text-gray-400 mt-2">
                    * 时段以 AOI 当地时间为准 (zipcode 决定时区), 结束 &lt; 开始 表示跨日。
                </div>
            </Card>

            {/*
              * 门禁 code - AOI 自己的门禁, 落 biz_aoi_attribute.access_codes, 跟其他衍生属性同一次 upsert 写入。
              * 只在 building (level=0) 显示: 门禁是楼栋级信息, community (level=1) 的码应记在具体子 AOI 上。
              */}
            {isBuilding && (
                <Card
                    size="small"
                    title={<EditorSectionTitle text="门禁 code" color={AOI_DETAIL_COLORS.relatedAddress} count={draft.accessCodes.length} max={MAX_LIST_SIZE} />}
                    extra={
                        <Button
                            size="small"
                            icon={<PlusOutlined />}
                            disabled={draft.accessCodes.length >= MAX_LIST_SIZE}
                            onClick={() => updateDraft({ accessCodes: [...draft.accessCodes, emptyAccessCodeDraft()] })}
                        >
                            新增
                        </Button>
                    }
                >
                    <AccessCodeEditor
                        codes={draft.accessCodes}
                        onChange={(next) => updateDraft({ accessCodes: next })}
                    />
                </Card>
            )}

            {isBuilding && (
                <Card
                    size="small"
                    title={<span>关联地址 <span className="text-xs text-gray-400 ml-1">({detail.relatedAddresses.length})</span></span>}
                >
                    <RelatedAddressList addresses={detail.relatedAddresses} />
                    <div className="mt-3 pt-3 border-t border-dashed border-gray-200">
                        <RelatedAddressAdder
                            aoiId={detail.aoiId}
                            zipcode={detail.zipcode ?? ''}
                            onSuccess={onRefetchDetail}
                        />
                    </div>
                </Card>
            )}
            {isCommunity && (
                <Card
                    size="small"
                    title={<span>子 AOI <span className="text-xs text-gray-400 ml-1">({detail.childAois.length})</span></span>}
                >
                    {detail.childAois.length === 0
                        ? <span className="text-xs text-gray-400">无</span>
                        : <div className="space-y-1">
                            {detail.childAois.map((c) => (
                                <div key={c.aoiId} className="text-sm"><Tag color="cyan">{c.aoiId}</Tag>{c.displayName ?? '-'}</div>
                            ))}
                        </div>}
                </Card>
            )}
        </div>
    );
};

/**
 * 关联地址列表 - 超过 {@link RELATED_ADDRESS_PREVIEW_LIMIT} 条只展示前 N 条 + 「还有 X 个」缩略,
 * 跟地图 popup 的展示逻辑保持一致, 避免几百条地址撑爆面板。
 */
const RELATED_ADDRESS_PREVIEW_LIMIT = 5;
const RelatedAddressList: React.FC<{ addresses: BizAoiAttributeDetail['relatedAddresses'] }> = ({ addresses }) => {
    const [expanded, setExpanded] = useState(false);
    if (addresses.length === 0) {
        return <span className="text-xs text-gray-400">无</span>;
    }
    const overflow = addresses.length - RELATED_ADDRESS_PREVIEW_LIMIT;
    const visible = expanded ? addresses : addresses.slice(0, RELATED_ADDRESS_PREVIEW_LIMIT);
    return (
        <div className="space-y-1">
            {visible.map((a) => (
                <div key={a.addressCoreId} className="text-sm">{a.fullStandardAddress}</div>
            ))}
            {overflow > 0 && (
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="text-sm text-blue-600 hover:text-blue-700 hover:underline cursor-pointer mt-1"
                >
                    {expanded ? '收起' : `还有 ${overflow} 个地址 (点击展开)`}
                </button>
            )}
        </div>
    );
};

/**
 * 关联地址新增 - TextArea + "添加" 按钮; 后端校验通过的入库 + 挂到 AOI, 失败的展示 error。
 *
 * <p>跟标准化地址平台的「新增标准化地址」交互一致: 一行一条, 按 \n 分隔; 提交后展示逐条结果。
 * 成功的会触发 onSuccess (= 父组件 refetch detail), 这时 relatedAddresses 列表自动更新。
 */
const RelatedAddressAdder: React.FC<{
    aoiId: number;
    zipcode: string;
    onSuccess?: () => void;
}> = ({ aoiId, zipcode, onSuccess }) => {
    const [text, setText] = useState('');
    const [results, setResults] = useState<AoiAddRelatedAddressItem[]>([]);
    const [submit, { loading }] = useMutation<AddAoiRelatedAddressesResponse>(ADD_AOI_RELATED_ADDRESSES);

    const handleSubmit = async () => {
        const lines = text.split('\n').map((s) => s.trim()).filter((s) => s.length > 0);
        if (lines.length === 0) {
            message.warning('请输入至少一条地址');
            return;
        }
        if (lines.length > MAX_LIST_SIZE) {
            message.error(`单次最多新增 ${MAX_LIST_SIZE} 条地址`);
            return;
        }
        if (!zipcode) {
            message.error('当前 AOI 缺少 zipcode, 无法新增地址');
            return;
        }
        try {
            const input: AoiAddRelatedAddressesInput = { aoiId, zipcode, addressStrings: lines };
            const res = await submit({ variables: { input } });
            const r = res.data?.addAoiRelatedAddresses;
            if (!r) {
                message.error('新增失败: 无响应');
                return;
            }
            setResults(r.items);
            if (r.successCount > 0) {
                message.success(`新增成功 ${r.successCount} 条${r.failedCount > 0 ? `, 失败 ${r.failedCount} 条 (见下方明细)` : ''}`);
                onSuccess?.();
                // 成功的清掉输入框, 失败的保留让用户重试或删除
                const failedOriginals = r.items.filter((i) => !i.success).map((i) => i.originalAddress);
                setText(failedOriginals.join('\n'));
            } else {
                message.error(`全部失败 (${r.failedCount} 条), 详见下方明细`);
            }
        } catch (e) {
            // 不把后端 / GraphQL / Dubbo 的系统错误原文展示给用户; 完整堆栈打到 console 给前端开发看
            console.error('addAoiRelatedAddresses failed:', e);
            message.error('新增失败, 请稍后重试; 如果一直不成功请联系运维');
        }
    };

    return (
        <div className="space-y-2">
            <div className="text-xs text-gray-500">
                新增关联地址 (一行一条, 标准化校验后挂到当前 AOI; 单次最多 {MAX_LIST_SIZE} 条):
            </div>
            <Input.TextArea
                rows={3}
                placeholder="如: 734 W Alameda Dr, Tempe, AZ 85282"
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={loading}
            />
            <div className="flex justify-end">
                <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    loading={loading}
                    onClick={handleSubmit}
                    disabled={!zipcode}
                >添加</Button>
            </div>
            {results.length > 0 && (
                <div className="space-y-1 mt-2">
                    {results.map((r, idx) => (
                        <div
                            key={idx}
                            className={`text-xs px-2 py-1 rounded ${r.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}
                        >
                            <span className="font-medium">{r.success ? '✓' : '✗'}</span>
                            <span className="ml-1">{r.fullStandardAddress ?? r.originalAddress}</span>
                            {r.errorMessage && <span className="ml-2 text-red-500">— {r.errorMessage}</span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

/* ====================== 工具函数 ====================== */

/**
 * 初始化 draft - 父组件 (`AoiPlatformDetailPage`) 在 detail 加载后调用一次。
 *
 * <p>独立 export 出来方便父组件在 view↔edit 切换 / detail 重新拉取时重置。
 * 配送时间从 JSON 字符串 parse 成结构化对象, 保存时再序列化回字符串。
 * 门禁 code 直接来自 biz_aoi_attribute.access_codes, 转成 UI 草稿形态 (uid 不留, 服务端每次重算)。
 */
export function initDraftFromDetail(detail: BizAoiAttributeDetail): AoiAttributeDraft {
    const attr = detail.attribute;
    const accessCodes: AccessCodeDraft[] = (attr?.accessCodes ?? []).map((c) => ({
        codeType: c.codeType ?? '',
        codeValue: c.codeValue ?? '',
        available: c.available ?? 'PENDING_VERIFICATION',
        remark: c.remark ?? null,
    }));

    return {
        typeCode: attr?.typeCode ?? null,
        entryPoints: attr?.entryPoints ?? [],
        parkingPoints: attr?.parkingPoints ?? [],
        deliveryTime: parseDeliveryTime(attr?.deliveryTime),
        accessCodes,
    };
}

/** 字典 (扁平 list with parentCode) → AntD Cascader 嵌套 options。 */
function buildCascaderOptions(items: AoiTypeDictItem[]): { value: string; label: string; children?: { value: string; label: string }[] }[] {
    const roots = items.filter((i) => i.isRoot);
    return roots.map((r) => {
        const children = items.filter((i) => i.parentCode === r.code);
        return {
            value: r.code,
            label: r.zhName,
            children: children.length > 0 ? children.map((c) => ({ value: c.code, label: c.zhName })) : undefined,
        };
    });
}

/* ====================== 子组件 ====================== */

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <div className="flex justify-between text-sm py-0.5">
        <span className="text-gray-500">{label}</span>
        <span className="text-gray-900 text-right ml-2">{value}</span>
    </div>
);

const EditorSectionTitle: React.FC<{ text: string; color: string; count: number; max?: number }> = ({ text, color, count, max }) => (
    <div className="flex items-center gap-2">
        <span className="inline-block w-3 h-3 rounded-full" style={{ background: color }} />
        <span>{text}</span>
        <Tag>{max != null ? `${count}/${max}` : count}</Tag>
    </div>
);

/**
 * `+ 新增` 按钮 - 进入 / 退出拾取模式。
 *
 * <p>active 状态用 primary 高亮提醒用户 "正在拾取", disabled 时通过 disabledReason 给出 tooltip。
 */
const PickAddButton: React.FC<{
    active: boolean;
    disabled: boolean;
    disabledReason?: string;
    onClick: () => void;
}> = ({ active, disabled, disabledReason, onClick }) => {
    const btn = (
        <Button
            size="small"
            type={active ? 'primary' : 'default'}
            icon={<AimOutlined />}
            disabled={disabled}
            onClick={onClick}
        >
            {active ? '点击地图选择...' : '从地图选取'}
        </Button>
    );
    if (disabled && disabledReason) {
        return <Tooltip title={disabledReason}>{btn}</Tooltip>;
    }
    return btn;
};

const PointsEditor: React.FC<{
    points: BizAoiAttributeLocationPoint[];
    onChange: (idx: number, patch: Partial<BizAoiAttributeLocationPoint>) => void;
    onRemove: (idx: number) => void;
}> = ({ points, onChange, onRemove }) => {
    if (points.length === 0) {
        return <span className="text-xs text-gray-400">无</span>;
    }
    return (
        <div className="space-y-2">
            {points.map((p, idx) => (
                <div key={idx} className="p-2 border rounded space-y-1">
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">#{idx + 1}</span>
                        <Popconfirm
                            title="确认删除该点位?"
                            okText="确定"
                            cancelText="取消"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => onRemove(idx)}
                        >
                            <Button
                                size="small"
                                danger
                                type="link"
                                icon={<DeleteOutlined />}
                            >删除</Button>
                        </Popconfirm>
                    </div>
                    <Space.Compact className="w-full">
                        <InputNumber
                            className="w-full"
                            placeholder="经度 (lng)"
                            value={p.lng ?? undefined}
                            step={0.0000001}
                            onChange={(v) => onChange(idx, { lng: v ?? null })}
                        />
                        <InputNumber
                            className="w-full"
                            placeholder="纬度 (lat)"
                            value={p.lat ?? undefined}
                            step={0.0000001}
                            onChange={(v) => onChange(idx, { lat: v ?? null })}
                        />
                    </Space.Compact>
                    <Input
                        placeholder="备注 (如: 北门 / 侧门)"
                        value={p.remark ?? undefined}
                        onChange={(e) => onChange(idx, { remark: e.target.value || null })}
                    />
                </div>
            ))}
        </div>
    );
};

export default AoiAttributeEditPanel;
